import React from "react";
import {
    AuditLog,
    ResourceType,
    AuditLogChangeType,
    AuditLogDiff,
} from "@common/types/auditLog";
import { ReconciliationTransactionLogItem } from "./ReconciliationTransactionLogItem";
import { BankReconciliationLogItem } from "./BankReconciliationLogItem";
import { BankReconciliationLogType } from "./BankReconciliationLogItem.lib";

interface ReconciliationLogProps {
    log: AuditLog;
}

interface LogRendererConfig {
    shouldRender: (log: AuditLog) => boolean;
    render: (log: AuditLog) => React.ReactElement | null;
}

const shouldDisplayTransactionLog = (log: AuditLog): boolean => {
    if (log.resourceType !== ResourceType.TRANSACTION) {
        return false;
    }

    const isReconciliation = log.diff?.some(
        (d: AuditLogDiff) => d.field === "reconciledInId" && d.after !== null,
    );
    const isCreation = log.changeType === AuditLogChangeType.CREATE;
    const isDeletion = log.changeType === AuditLogChangeType.DELETE;

    return isReconciliation || isCreation || isDeletion;
};

const shouldDisplayBankReconciliationCreated = (log: AuditLog): boolean =>
    log.resourceType === ResourceType.BANK_RECONCILIATION &&
    log.changeType === AuditLogChangeType.CREATE;

const shouldDisplayBankReconciliationCompleted = (log: AuditLog): boolean =>
    log.resourceType === ResourceType.BANK_RECONCILIATION &&
    log.changeType === AuditLogChangeType.UPDATE &&
    !!log.diff?.some(
        (d: AuditLogDiff) => d.field === "closingBalance" && d.after !== null,
    );

const logRenderers: LogRendererConfig[] = [
    {
        shouldRender: shouldDisplayTransactionLog,
        render: (log) => <ReconciliationTransactionLogItem log={log} />,
    },
    {
        shouldRender: shouldDisplayBankReconciliationCreated,
        render: (log) => (
            <BankReconciliationLogItem
                log={log}
                type={BankReconciliationLogType.CREATED}
            />
        ),
    },
    {
        shouldRender: shouldDisplayBankReconciliationCompleted,
        render: (log) => (
            <BankReconciliationLogItem
                log={log}
                type={BankReconciliationLogType.COMPLETED}
            />
        ),
    },
];

export const ReconciliationLog: React.FC<ReconciliationLogProps> = ({
    log,
}) => {
    for (const { shouldRender, render } of logRenderers) {
        if (shouldRender(log)) {
            return render(log);
        }
    }

    return null;
};
